// src/Pages/Dashboard/Sales/Credit_Notes.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Credit_Notes.css";

const Credit_Notes = () => {
  const [creditNotes, setCreditNotes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const API_URL =
    process.env.REACT_APP_BACKEND_URL ||
    (process.env.NODE_ENV === "production"
      ? "https://finance.dev.marsforce.in"
      : "http://localhost:8030");

  useEffect(() => {
    fetchCreditNotes();
  }, []);

  const fetchCreditNotes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/credit-notes`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setCreditNotes(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredCreditNotes = creditNotes.filter((cn) => {
    const customerName = cn.customer?.name || "Walk-in Customer";
    return (
      cn.creditNoteNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cn.referenceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const formatDate = (date) => new Date(date).toLocaleDateString("en-IN");
  const formatAmount = (amt) =>
    "₹" +
    Number(amt || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

  const getStatusClass = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "paid") return "status-paid";
    if (s === "sent") return "status-sent";
    if (s === "cancelled") return "status-cancelled";
    return "status-draft";
  };

  if (loading) {
    return (
      <div className="credit-wrapper">
        <div className="credit-container">
          <div className="state-card">
            <p>Loading credit notes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="credit-wrapper">
      <div className="credit-container">
        {/* Header */}
        <div className="credit-header">
          <h1>Credit Notes ({filteredCreditNotes.length})</h1>
          <div className="header-actions">
            <input
              type="text"
              placeholder="Search by Credit Note#, Reference# or Customer..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Link to="/sales/credit-notes/new">
              <button className="new-btn">+ New Credit Note</button>
            </Link>
          </div>
        </div>

        {/* Empty State */}
        {filteredCreditNotes.length === 0 && !loading && (
          <div className="state-card empty">
            <h3>
              {searchTerm ? "No credit notes found" : "No Credit Notes Yet"}
            </h3>
            <p>
              {searchTerm
                ? `No match for "${searchTerm}"`
                : "Start issuing credit notes to customers."}
            </p>
            <Link to="/sales/credit-notes/new">
              <button className="new-btn">+ Create First Credit Note</button>
            </Link>
          </div>
        )}

        {/* List – Grid for Desktop, Cards for Mobile */}
        {filteredCreditNotes.length > 0 && (
          <div className="credit-list">
            {/* Desktop Header */}
            <div className="table-header">
              <span></span>
              <span>DATE</span>
              <span>CREDIT NOTE#</span>
              <span>REFERENCE#</span>
              <span>CUSTOMER</span>
              <span>STATUS</span>
              <span>AMOUNT</span>
              <span>ACTION</span>
            </div>

            {/* Rows – Grid on Desktop, Cards on Mobile */}
            {filteredCreditNotes.map((cn) => (
              <div
                key={cn._id}
                className="credit-row"
                onClick={() =>
                  navigate(`/sales/credit-notes/${cn.creditNoteNumber}`)
                }
              >
                <input type="checkbox" onClick={(e) => e.stopPropagation()} />

                <span data-label="Date">{formatDate(cn.creditNoteDate)}</span>

                <span data-label="Credit Note#" className="credit-no">
                  <Link
                    to={`/sales/credit-notes/${cn.creditNoteNumber}`}
                    onClick={(e) => e.stopPropagation()}
                    className="name-link"
                  >
                    {cn.creditNoteNumber}
                  </Link>
                </span>

                <span data-label="Reference#">{cn.referenceNumber || "—"}</span>

                <span data-label="Customer">
                  {cn.customer?.name || "Walk-in Customer"}
                </span>

                <span
                  data-label="Status"
                  className={`status-badge ${getStatusClass(cn.status)}`}
                >
                  {cn.status || "Draft"}
                </span>

                <span data-label="Amount" className="amount">
                  {formatAmount(cn.total)}
                </span>

                <span data-label="Action" onClick={(e) => e.stopPropagation()}>
                  <Link
                    to={`/sales/credit-notes/${cn.creditNoteNumber}`}
                    className="view-link"
                  >
                    View →
                  </Link>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Credit_Notes;
